import { useEffect, useState } from "react";
import { toast } from "sonner";
import { User, Mail, Phone, Key, Shield, Crown } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

/**
 * AdminProfile — same self-service editor pattern as ClientProfile /
 * EmployeeProfile, scoped to admin users. Edits name, phone and password
 * via the shared `/auth/me` PUT endpoint. Email stays read-only (changing
 * it would require a re-issued session and risks lockout).
 */
export default function AdminProfile() {
  const { user, refreshMe } = useAuth();
  const [form, setForm] = useState({
    name: "", phone: "",
    new_password: "", confirm_password: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) setForm(f => ({ ...f, name: user.name || "", phone: user.phone || "" }));
  }, [user]);

  async function save(e) {
    e.preventDefault();
    if (form.new_password && form.new_password !== form.confirm_password) {
      toast.error("As palavras-passe não coincidem.");
      return;
    }
    if (form.new_password && form.new_password.length < 6) {
      toast.error("A palavra-passe deve ter pelo menos 6 caracteres.");
      return;
    }
    setBusy(true);
    try {
      const payload = { name: form.name, phone: form.phone };
      if (form.new_password) payload.password = form.new_password;
      await api.put("/auth/me", payload);
      toast.success("Perfil atualizado.");
      setForm(f => ({ ...f, new_password: "", confirm_password: "" }));
      refreshMe?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  }

  if (!user) return null;

  return (
    <div className="space-y-10 max-w-3xl" data-testid="admin-profile-page">
      <div>
        <div className="eyebrow mb-3">Conta administrativa</div>
        <h1 className="font-display text-5xl">O meu perfil.</h1>
        <div className="text-sm text-[#a0b2af] mt-2">
          Atualize o seu nome, telemóvel e palavra-passe. O e-mail é fixo —
          se precisar de alterar contacte o suporte.
        </div>
      </div>

      <div className="lux-card p-6 flex items-center gap-5 flex-wrap">
        <div className="w-16 h-16 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/40 grid place-items-center">
          <Crown size={28} className="text-[#d4af37]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-3xl">{user.name}</div>
          <div className="text-xs text-[#a0b2af] mt-1">
            <Mail size={10} className="inline mr-1" /> {user.email}
          </div>
          <div className="text-[10px] uppercase tracking-widest mt-2 inline-flex items-center gap-1 text-[#d4af37]">
            <Shield size={10} /> Administrador
          </div>
        </div>
      </div>

      <form onSubmit={save} className="lux-card p-6 space-y-5" data-testid="admin-profile-form">
        <div className="eyebrow">Editar dados</div>

        <Field icon={<User size={14} />} label="Nome" value={form.name}
               onChange={v => setForm(f => ({ ...f, name: v }))}
               testId="admin-profile-name" required />
        <Field icon={<Phone size={14} />} label="Telemóvel" value={form.phone} type="tel"
               onChange={v => setForm(f => ({ ...f, phone: v }))}
               testId="admin-profile-phone" />
        <Field icon={<Mail size={14} />} label="Email (apenas leitura)"
               value={user.email} disabled />

        <div className="pt-4 border-t border-[#1a4640] space-y-5">
          <div className="eyebrow text-[10px]">Alterar palavra-passe</div>
          <Field icon={<Key size={14} />} label="Nova palavra-passe (mín. 6 caracteres)"
                 value={form.new_password} type="password" placeholder="Deixe vazio para manter"
                 onChange={v => setForm(f => ({ ...f, new_password: v }))}
                 testId="admin-profile-password" />
          {form.new_password && (
            <Field icon={<Key size={14} />} label="Confirmar nova palavra-passe"
                   value={form.confirm_password} type="password"
                   onChange={v => setForm(f => ({ ...f, confirm_password: v }))}
                   testId="admin-profile-password-confirm" />
          )}
        </div>

        <button type="submit" disabled={busy}
                className="btn-gold px-6 py-3 text-sm disabled:opacity-60"
                data-testid="admin-profile-save">
          {busy ? "A guardar…" : "Guardar alterações"}
        </button>
      </form>
    </div>
  );
}

function Field({ icon, label, value, onChange, type = "text", testId,
                 required = false, disabled = false, placeholder = "" }) {
  return (
    <div className="space-y-2">
      <label className="text-xs uppercase tracking-widest text-[#a0b2af] flex items-center gap-2">
        <span className="text-[#d4af37]">{icon}</span>{label}
      </label>
      <input
        type={type}
        value={value || ""}
        onChange={e => onChange?.(e.target.value)}
        required={required}
        disabled={disabled}
        placeholder={placeholder}
        className="w-full bg-[#062c27] border border-[#1a4640] rounded-sm px-4 py-3 outline-none focus:border-[#d4af37] disabled:opacity-60 disabled:cursor-not-allowed"
        data-testid={testId}
      />
    </div>
  );
}
